<script setup lang="ts">
import { computed, ref, useTemplateRef, watch } from "vue";
import { UiAside, UiButton, UiDialog, UiIcon, UiText } from "@shopexperts/shared";
import type { SatelliteContactSite } from "@shopexperts/legacy-api";
import SatelliteStatsOverview from "@/app/components/satellite-contact-sites/SatelliteStatsOverview.vue";
import SatelliteSiteToolbar from "@/app/components/satellite-contact-sites/SatelliteSiteToolbar.vue";
import SatelliteSiteCard from "@/app/components/satellite-contact-sites/SatelliteSiteCard.vue";
import SatelliteSiteForm from "@/app/components/satellite-contact-sites/SatelliteSiteForm.vue";
import SatelliteSiteDetailPanel from "@/app/components/satellite-contact-sites/SatelliteSiteDetailPanel.vue";
import { useSatelliteContactSite } from "@/app/composables/satellite-contact-sites/useSatelliteContactSites";
import { useSatelliteSitesPage } from "@/app/composables/satellite-contact-sites/useSatelliteSitesPage";
import {
    buildDeleteSiteCopy,
    buildRegenerateKeyCopy,
    defaultSatelliteSiteFormValues,
    formValuesToCreatePayload,
    formValuesToUpdatePayload,
    siteToFormValues,
    type SatelliteSiteFormValues,
} from "@/app/composables/satellite-contact-sites/satelliteSiteForm";

type PanelMode = "create" | "edit" | "detail";

const {
    searchInput,
    activeFilter,
    sites,
    statsBySiteId,
    aggregateStats,
    isListLoading,
    isListFetching,
    isStatsLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    createSite,
    updateSite,
    deleteSite,
    regenerateKey,
} = useSatelliteSitesPage();

const panelOpen = ref(false);
const panelMode = ref<PanelMode>("create");
const selectedSiteId = ref<number | null>(null);
const editingSite = ref<SatelliteContactSite | null>(null);
const highlightAccessKey = ref(false);
const formModel = ref<SatelliteSiteFormValues>(defaultSatelliteSiteFormValues());
const formEl = useTemplateRef("formEl");

const deleteOpen = ref(false);
const sitePendingDelete = ref<SatelliteContactSite | null>(null);

const regenerateOpen = ref(false);
const sitePendingRegenerate = ref<SatelliteContactSite | null>(null);

const { data: selectedSite, isLoading: isDetailLoading } = useSatelliteContactSite(selectedSiteId);

const isFormPanel = computed(() => panelMode.value === "create" || panelMode.value === "edit");
const isSubmitting = computed(() => createSite.isPending.value || updateSite.isPending.value);
const deleteCopy = computed(() => buildDeleteSiteCopy(sitePendingDelete.value));
const regenerateCopy = computed(() => buildRegenerateKeyCopy(sitePendingRegenerate.value));
const selectedSiteStats = computed(() => {
    const siteId = selectedSiteId.value;
    if (siteId == null) return null;
    return statsBySiteId.value.get(siteId) ?? null;
});

watch(panelOpen, (open) => {
    if (!open) {
        selectedSiteId.value = null;
        editingSite.value = null;
        highlightAccessKey.value = false;
        formEl.value?.resetForm();
    }
});

function openCreate() {
    panelMode.value = "create";
    editingSite.value = null;
    selectedSiteId.value = null;
    highlightAccessKey.value = false;
    formModel.value = defaultSatelliteSiteFormValues();
    formEl.value?.resetForm();
    panelOpen.value = true;
}

function openEdit(site: SatelliteContactSite) {
    panelMode.value = "edit";
    editingSite.value = site;
    selectedSiteId.value = site.id;
    highlightAccessKey.value = false;
    formModel.value = siteToFormValues(site);
    panelOpen.value = true;
}

function openDetail(site: SatelliteContactSite) {
    panelMode.value = "detail";
    editingSite.value = null;
    selectedSiteId.value = site.id;
    highlightAccessKey.value = false;
    panelOpen.value = true;
}

function openDelete(site: SatelliteContactSite) {
    sitePendingDelete.value = site;
    deleteOpen.value = true;
}

function openRegenerate(site: SatelliteContactSite) {
    sitePendingRegenerate.value = site;
    regenerateOpen.value = true;
}

function closePanel() {
    panelOpen.value = false;
}

async function onFormSubmit(values: SatelliteSiteFormValues) {
    try {
        if (panelMode.value === "edit" && editingSite.value) {
            const updated = await updateSite.mutateAsync({
                siteId: editingSite.value.id,
                body: formValuesToUpdatePayload(values),
            });
            if (values.use_custom_access_key && updated.access_key) {
                highlightAccessKey.value = true;
                panelMode.value = "detail";
                selectedSiteId.value = updated.id;
                return;
            }
        } else {
            const created = await createSite.mutateAsync(formValuesToCreatePayload(values));
            highlightAccessKey.value = true;
            panelMode.value = "detail";
            selectedSiteId.value = created.id;
            return;
        }
        closePanel();
    } catch (error) {
        console.error("[SatelliteContactSitesPage] Failed to save site:", error);
    }
}

async function confirmDelete() {
    const target = sitePendingDelete.value;
    if (!target) return;
    try {
        await deleteSite.mutateAsync(target.id);
        deleteOpen.value = false;
        sitePendingDelete.value = null;
        if (selectedSiteId.value === target.id) {
            closePanel();
        }
    } catch (error) {
        console.error("[SatelliteContactSitesPage] Failed to delete site:", error);
    }
}

async function confirmRegenerate() {
    const target = sitePendingRegenerate.value;
    if (!target) return;
    try {
        const updated = await regenerateKey.mutateAsync(target.id);
        regenerateOpen.value = false;
        sitePendingRegenerate.value = null;
        highlightAccessKey.value = true;
        selectedSiteId.value = updated.id;
        panelMode.value = "detail";
        panelOpen.value = true;
    } catch (error) {
        console.error("[SatelliteContactSitesPage] Failed to regenerate key:", error);
    }
}

function cancelDelete() {
    deleteOpen.value = false;
    sitePendingDelete.value = null;
}

function cancelRegenerate() {
    regenerateOpen.value = false;
    sitePendingRegenerate.value = null;
}

const panelTitle = computed(() => {
    if (panelMode.value === "create") return "Create satellite site";
    if (panelMode.value === "edit") return "Edit satellite site";
    return "Site details";
});

const panelDescription = computed(() => {
    if (panelMode.value === "create") {
        return "Register a partner website and issue its first access key.";
    }
    if (panelMode.value === "edit") {
        return "Update site metadata, status, or rotate the key manually.";
    }
    return "Live site record from the admin API, including usage stats.";
});
</script>

<template>
    <main class="flex-1 p-8 overflow-y-auto bg-secondary font-light">
        <div class="flex flex-col gap-4 mb-6">
            <SatelliteSiteToolbar
                v-model:search-input="searchInput"
                v-model:active-filter="activeFilter"
                @create="openCreate"
            />

            <SatelliteStatsOverview :stats="aggregateStats" :is-loading="isStatsLoading" />
        </div>

        <div class="space-y-3">
                <div v-if="isListLoading" class="py-12 text-center">
                    <UiText as="p" variant="p" class="text-se-gray-600" text="Loading satellite sites..." />
                </div>

                <div v-else-if="sites.length === 0" class="py-16 text-center">
                    <div class="mx-auto flex max-w-md flex-col items-center gap-3">
                        <div class="flex h-12 w-12 items-center justify-center rounded-full bg-se-gray-100">
                            <UiIcon name="link-external" class="h-6 w-6 text-se-gray-500" />
                        </div>
                        <UiText
                            as="h6"
                            variant="h6"
                            class="font-semibold"
                            :text="searchInput ? 'No sites match your search' : 'No satellite sites yet'"
                        />
                        <UiText
                            as="p"
                            variant="p-small"
                            class="text-se-gray-600"
                            :text="searchInput
                                ? 'Try another name or URL, or clear the filters.'
                                : 'Add your first partner website to start accepting authenticated contact submissions.'"
                        />
                        <UiButton v-if="!searchInput" variant="primary" label="Add Satellite Site" @click="openCreate">
                            <template #before_label>
                                <UiIcon name="plus-square" class="h-5 w-5" />
                            </template>
                        </UiButton>
                    </div>
                </div>

                <template v-else>
                    <SatelliteSiteCard
                        v-for="site in sites"
                        :key="site.id"
                        :site="site"
                        :stats="statsBySiteId.get(site.id)"
                        @view="openDetail(site)"
                        @edit="openEdit(site)"
                        @delete="openDelete(site)"
                        @regenerate-key="openRegenerate(site)"
                    />

                    <div v-if="hasNextPage" class="flex justify-center pt-2">
                        <UiButton
                            variant="outline"
                            :label="isFetchingNextPage ? 'Loading more...' : 'Load more sites'"
                            :disabled="isFetchingNextPage"
                            @click="fetchNextPage()"
                        />
                    </div>

                    <div v-if="isListFetching && !isListLoading && !isFetchingNextPage" class="text-center pt-2">
                        <UiText as="p" variant="caption-normal" class="text-se-gray-500" text="Refreshing..." />
                    </div>
                </template>
        </div>

        <UiAside v-model="panelOpen" position="right" :close-on-overlay-click="false">
            <div class="flex max-h-full flex-col gap-4 overflow-y-auto px-4 py-6 md:p-8">
                <div class="flex items-start justify-between gap-4">
                    <div class="flex flex-col gap-1">
                        <UiText as="h3" variant="h3" class="font-semibold" :text="panelTitle" />
                        <UiText as="p" variant="p-small" class="text-se-gray-600" :text="panelDescription" />
                    </div>
                    <UiButton variant="ghost" shape="square" :disabled="isSubmitting" @click="closePanel">
                        <template #before_label>
                            <UiIcon name="close" class="h-6 w-6" />
                        </template>
                    </UiButton>
                </div>

                <SatelliteSiteForm
                    v-if="isFormPanel"
                    ref="formEl"
                    v-model="formModel"
                    :is-editing="panelMode === 'edit'"
                    :is-submitting="isSubmitting"
                    @submit="onFormSubmit"
                    @cancel="closePanel"
                />

                <SatelliteSiteDetailPanel
                    v-else
                    :site="selectedSite"
                    :stats="selectedSiteStats"
                    :is-loading="isDetailLoading"
                    :highlight-access-key="highlightAccessKey"
                />
            </div>
        </UiAside>

        <UiDialog
            v-model="deleteOpen"
            v-model:loading="deleteSite.isPending"
            :title="deleteCopy.title"
            :text="deleteCopy.text"
            :primary-action-label="deleteCopy.primaryActionLabel"
            :secondary-action-label="deleteCopy.secondaryActionLabel"
            @primary-action="confirmDelete"
            @secondary-action="cancelDelete"
        />

        <UiDialog
            v-model="regenerateOpen"
            v-model:loading="regenerateKey.isPending"
            :title="regenerateCopy.title"
            :text="regenerateCopy.text"
            :primary-action-label="regenerateCopy.primaryActionLabel"
            :secondary-action-label="regenerateCopy.secondaryActionLabel"
            @primary-action="confirmRegenerate"
            @secondary-action="cancelRegenerate"
        />
    </main>
</template>
